/**
 * Bundle Cart Management
 * Handles pricing calculation and cart operations for bundle products
 */

class BundleCart {
  constructor(container) {
    this.container = container;
    this.formId = container.dataset.bundleForm;
    this.form = document.getElementById(this.formId);
    this.config = this.loadConfig();
    this.priceElement = container.querySelector('[data-bundle-price]');
    this.comparePriceElement = container.querySelector('[data-bundle-compare-price]');
    this.addToCartButton = container.querySelector('[data-bundle-add-to-cart]');
    this.buttonText = container.querySelector('[data-button-text]');
    this.spinner = container.querySelector('.button__spinner');
    this.preOrderMessage = container.querySelector('[data-pre-order-message]');
    this.klarnaElement = container.querySelector('[data-bundle-klarna]');

    // Page-level price element shown below the product title
    const section = container.closest('.shopify-section');
    this.pagePriceElement = section
      ? section.querySelector('.price [data-money-convertible]:not(.price--compare [data-money-convertible])')
      : null;
    this.pageComparePriceElement = section
      ? section.querySelector('.price--compare [data-money-convertible]')
      : null;

    this.init();
  }

  loadConfig() {
    const configElement = this.container.querySelector('[data-bundle-config]');
    if (!configElement) return {};
    try {
      return JSON.parse(configElement.textContent);
    } catch (e) {
      console.error('BundleCart: failed to parse config JSON', e);
      return {};
    }
  }

  init() {
    // Listen for variant changes
    document.addEventListener('bundle:variant-changed', () => {
      this.updatePricing();
    });

    // Listen for variant select changes
    const variantSelects = document.querySelectorAll(`[form="${this.formId}"][data-variant-select]`);
    variantSelects.forEach(select => {
      select.addEventListener('change', () => {
        this.updatePricing();
      });
    });

    // Add to cart handler
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.addBundleToCart();
      });
    }

    // Initial price calculation
    this.updatePricing();
  }

  getAllSelectedVariants() {
    const variantSelects = document.querySelectorAll(
      `[form="${this.formId}"][data-variant-select]:not([disabled])`
    );
    
    const variants = [];
    variantSelects.forEach(select => {
      const option = select.options[select.selectedIndex];
      if (option && option.value) {
        const productHandle = select.dataset.productHandle;
        const productConfig = this.config.products?.find(p => p.handle === productHandle);
        
        variants.push({
          id: parseInt(option.value),
          handle: productHandle,
          price: parseInt(option.dataset.price || 0),
          inventory: parseInt(option.dataset.inventory || 0),
          available: !option.disabled,
          sku: option.dataset.sku || '',
          productType: productConfig?.type || '',
          productTitle: productConfig?.title || ''
        });
      }
    });

    return variants;
  }

  // Returns { raw, sale } in cents. raw = full price (pillows × 2), sale = raw × 0.9
  calculateBundlePrice(variants) {
    let raw = 0;

    variants.forEach(variant => {
      // Pillows come as a set of 2, so count their price twice
      const qty = variant.handle && variant.handle.includes('pillow') ? 2 : 1;
      raw += variant.price * qty;
    });

    raw = Math.round(raw);
    const sale = Math.round(raw * 0.9);
    return { raw, sale };
  }

  checkPreOrderStatus(variants) {
    let latestPreOrder = null;
    let latestDate = null;

    variants.forEach(variant => {
      // Check if variant has low/no inventory
      if (variant.inventory <= 0) {
        // Get product data to check pre-order metafields
        const productJson = document.querySelector(`[data-product-json][data-handle="${variant.handle}"]`);
        if (productJson) {
          const productData = JSON.parse(productJson.textContent);
          const variantData = productData.variants.find(v => v.id === variant.id);
          
          if (variantData?.metafields?.pre_order?.ship_date) {
            const shipDate = new Date(variantData.metafields.pre_order.ship_date);
            
            if (!latestDate || shipDate > latestDate) {
              latestDate = shipDate;
              latestPreOrder = {
                message: variantData.metafields.pre_order.display_message,
                buttonLabel: variantData.metafields.pre_order.button_label
              };
            }
          }
        }
      }
    });

    return latestPreOrder;
  }

  updatePricing() {
    const variants = this.getAllSelectedVariants();
    const { raw, sale } = this.calculateBundlePrice(variants);

    // Update bundle price display (above ATC)
    if (this.priceElement) {
      this.priceElement.textContent = this.formatMoney(sale);
    }

    // Sync the page-level sale price shown below the product title
    if (this.pagePriceElement) {
      this.pagePriceElement.textContent = this.formatMoney(sale);
    }

    // Sync the page-level compare (crossout) price
    if (this.pageComparePriceElement) {
      this.pageComparePriceElement.textContent = this.formatMoney(raw);
    }

    // Update Klarna amount
    if (this.klarnaElement) {
      this.klarnaElement.setAttribute('data-purchase-amount', sale);
    }

    // Determine per-step OOS state and update step badges
    const oosHandles = new Set(
      variants.filter(v => v.id > 0 && !v.available).map(v => v.handle)
    );
    document.querySelectorAll(`[form="${this.formId}"][data-variant-select]`).forEach(select => {
      const handle = select.dataset.productHandle;
      const step = select.closest('.bundle-step');
      if (!step) return;
      const isOOS = oosHandles.has(handle);
      step.classList.toggle('bundle-step--oos', isOOS);
      const badge = step.querySelector('.bundle-step__oos-badge');
      if (badge) badge.hidden = !isOOS;
    });

    // Determine overall OOS state (any selected variant is unavailable)
    const hasOOS = variants.some(v => v.id > 0 && !v.available);

    // Button text priority: OOS > pre-order > default
    if (hasOOS) {
      if (this.preOrderMessage) this.preOrderMessage.classList.add('hidden');
      if (this.buttonText) this.buttonText.textContent = 'Out of Stock';
    } else {
      const preOrder = this.checkPreOrderStatus(variants);
      if (preOrder && this.preOrderMessage) {
        this.preOrderMessage.classList.remove('hidden');
        const textEl = this.preOrderMessage.querySelector('[data-pre-order-text]');
        if (textEl) textEl.textContent = preOrder.message;
        if (this.buttonText && preOrder.buttonLabel) {
          this.buttonText.textContent = preOrder.buttonLabel;
        }
      } else {
        if (this.preOrderMessage) this.preOrderMessage.classList.add('hidden');
        if (this.buttonText) {
          const defaultText = this.buttonText.getAttribute('data-default-text') || 'Add to Cart';
          this.buttonText.textContent = defaultText;
        }
      }
    }

    // Disable/enable ATC button
    const allValid = this.validateSelections(variants);
    if (this.addToCartButton) {
      this.addToCartButton.disabled = !allValid;
    }
  }

  validateSelections(variants) {
    // Minimum: at least first and second products should be selected
    if (variants.length < 2) return false;

    // All variants must have a valid ID and be in stock
    return variants.every(v => v.id && v.id > 0 && v.available);
  }

  async addBundleToCart() {
    const variants = this.getAllSelectedVariants();
    
    if (!this.validateSelections(variants)) {
      alert('Please make all required selections');
      return;
    }

    // Show loading state
    if (this.spinner) this.spinner.classList.remove('hidden');
    if (this.addToCartButton) this.addToCartButton.disabled = true;

    try {
      // Build form data with all variants (pillows ship as a set of 2)
      const formData = {
        items: variants.map(variant => ({
          id: variant.id,
          quantity: variant.handle && variant.handle.includes('pillow') ? 2 : 1,
          properties: {
            '_bundle': 'true',
            '_bundle_parent': this.config.bundleProduct?.title || ''
          }
        }))
      };

      // Add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const result = await response.json();

      // Trigger cart update event
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { cart: result }
      }));

      // Show success message or open cart drawer
      this.handleAddToCartSuccess();

    } catch (error) {
      console.error('Bundle add to cart error:', error);
      alert('Sorry, there was an error adding the bundle to your cart. Please try again.');
    } finally {
      // Hide loading state
      if (this.spinner) this.spinner.classList.add('hidden');
      if (this.addToCartButton) this.addToCartButton.disabled = false;
    }
  }

  handleAddToCartSuccess() {
    // Dispatch cart refresh event so the theme updates cart count and drawer contents
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    // Open the cart drawer — use #cart-drawer element's show() method (Prestige theme)
    const cartDrawer = document.getElementById('cart-drawer');
    if (cartDrawer && typeof cartDrawer.show === 'function') {
      cartDrawer.show();
    } else {
      // Fallback: click the cart icon toggle
      const cartIcon = document.querySelector('[aria-controls="cart-drawer"]');
      if (cartIcon) cartIcon.click();
    }
  }

  formatMoney(cents) {
    // Basic money formatting - adjust based on your theme's requirements
    const dollars = cents / 100;
    return '$' + dollars.toFixed(2).replace(/\.00$/, '');
  }
}

// Initialize all bundle forms
document.addEventListener('DOMContentLoaded', () => {
  const bundleContainers = document.querySelectorAll('.bundle-buy-buttons[data-bundle-form]');
  bundleContainers.forEach(container => {
    new BundleCart(container);
  });
});
